import { useState } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { cn } from "../../utils/cn";
import TorrentImageGallery from "../TorrentImageGallery";
import { Grid3X3, Layers, LayoutList } from "lucide-react";
import {
  getPrimaryTorrentImageUrl,
  getTorrentImageUrls,
} from "../../utils/torrentImages";

const layoutIcons = {
  stack: Layers,
  grid: Grid3X3,
  list: LayoutList,
};

// Функция форматирования времени (как в основном окне)
const formatTimeAgo = (timeString) => {
  if (!timeString) return "";
  // Пример: "May 04 2025, 05:34"
  try {
    // Парсим время как UTC, чтобы избежать смещения временной зоны
    const parsed = Date.parse(
      timeString.replace(/(\d{2}) (\d{4}),/, "$1, $2") + " UTC",
    );
    const date = isNaN(parsed)
      ? new Date(timeString + " UTC")
      : new Date(parsed);
    if (isNaN(date.getTime())) return "";

    // Используем обычное текущее время, так как date уже в UTC
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // в секундах

    if (diff < 60) return "just now";
    if (diff < 3600)
      return `${Math.floor(diff / 60)} min${
        Math.floor(diff / 60) === 1 ? "" : "s"
      } ago`;
    if (diff < 86400)
      return `${Math.floor(diff / 3600)} hour${
        Math.floor(diff / 3600) === 1 ? "" : "s"
      } ago`;
    if (diff < 604800)
      return `${Math.floor(diff / 86400)} day${
        Math.floor(diff / 86400) === 1 ? "" : "s"
      } ago`;
    if (diff < 2592000)
      return `${Math.floor(diff / 604800)} week${
        Math.floor(diff / 604800) === 1 ? "" : "s"
      } ago`;
    if (diff < 31536000)
      return `${Math.floor(diff / 2592000)} month${
        Math.floor(diff / 2592000) === 1 ? "" : "s"
      } ago`;
    return `${Math.floor(diff / 31536000)} year${
      Math.floor(diff / 31536000) === 1 ? "" : "s"
    } ago`;
  } catch (error) {
    console.error("Error parsing date:", timeString, error);
    return "";
  }
};

export default function MorphingCardStack({
  cards = [],
  className,
  defaultLayout = "stack",
  onCardClick,
  onBookmarkClick,
  allFavorite = false, // Deprecated: use favoriteIds instead
  favoriteIds = null, // Set of favorite torrent IDs
}) {
  const [layout, setLayout] = useState(defaultLayout);

  // Проверка, находится ли карточка в избранном
  const isCardFavorite = (cardId) => {
    if (allFavorite) return true;
    if (favoriteIds) return favoriteIds.has(Number(cardId));
    return false;
  };

  if (!cards || cards.length === 0) {
    return null;
  }

  const containerStyles = {
    stack: "flex flex-col gap-4 items-center w-full",
    grid: "grid grid-cols-2 gap-3",
    list: "flex flex-col gap-3",
  };

  const displayCards = cards.map((c, i) => ({ ...c, stackPosition: i }));

  return (
    <div className={cn("space-y-4", className)}>
      {/* Layout Toggle */}
      <div className="sticky top-[48px] z-40 flex items-center justify-center gap-1 rounded-lg bg-gray-800/80 backdrop-blur-sm p-1 w-fit mx-auto">
        {Object.keys(layoutIcons).map((mode) => {
          const Icon = layoutIcons[mode];
          return (
            <button
              key={mode}
              onClick={() => setLayout(mode)}
              className={cn(
                "rounded-md p-2 transition-all",
                layout === mode
                  ? "bg-gray-700 text-white"
                  : "text-gray-400 hover:text-white hover:bg-gray-800",
              )}
              aria-label={`Switch to ${mode} layout`}
            >
              <Icon className="h-4 w-4" />
            </button>
          );
        })}
      </div>

      {/* Cards Container */}
      <LayoutGroup>
        <motion.div layout className={cn(containerStyles[layout], "mx-auto")}>
          <AnimatePresence mode="popLayout" initial={false}>
            {displayCards.map((card) => {
              const cardImageUrls = getTorrentImageUrls(card);
              const primaryImageUrl = getPrimaryTorrentImageUrl(card);

              return (
                <motion.div
                  key={`${layout}-${card.id}`}
                  layout="position"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{
                    opacity: 0,
                    scale: 0.95,
                    transition: { duration: 0.1 },
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 30,
                    mass: 0.8,
                  }}
                  onClick={() => {
                    // Только вызываем onCardClick, убираем expandedCard чтобы не было мерцания
                    onCardClick?.(card);
                  }}
                  className={cn(
                    "cursor-pointer group relative",
                    layout === "stack" && "w-full max-w-md",
                    layout === "grid" && "w-full",
                    layout === "list" && "w-full",
                  )}
                >
                  {/* Bookmark - показываем если есть onBookmarkClick или favoriteIds */}
                  {(onBookmarkClick || favoriteIds || allFavorite) && (
                    <button
                      className={`ui-bookmark absolute -top-1 right-4 z-10${isCardFavorite(card.id) ? " active" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onBookmarkClick) {
                          onBookmarkClick(card);
                        }
                      }}
                    >
                      <div className="bookmark">
                        <svg viewBox="0 0 32 32">
                          <path d="M27 4v27a1 1 0 0 1-1.625.781L16 24.281l-9.375 7.5A1 1 0 0 1 5 31V4a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4z"></path>
                        </svg>
                      </div>
                    </button>
                  )}

                  {layout === "list" ? (
                    /* LIST VIEW - обновленный стиль (как у Stack/Grid) */
                    <div
                      className="rounded-xl border p-4 shadow-xl hover:border-orange-500/50 transition-all duration-300"
                      style={{
                        backgroundColor: "#181818",
                        borderColor: "#282828",
                        boxShadow: "0 4px 24px 0 rgba(0,0,0,0.18)",
                      }}
                    >
                      <div className="flex items-start justify-between w-full relative gap-3">
                        <div className="flex flex-col gap-2 min-w-0 flex-1 pr-12 z-10 relative">
                          <h3 className="font-bold text-white truncate text-sm group-hover:text-orange-400 transition-colors">
                            {card.title}
                          </h3>
                          <div className="flex items-center gap-2 overflow-hidden whitespace-nowrap">
                            <span
                              style={{
                                padding: "0.2rem 0.8rem",
                                borderRadius: "999px",
                                background: "rgba(35,35,35,0.35)",
                                border: "1px solid rgba(40,40,40,0.7)",
                                fontVariantNumeric: "tabular-nums",
                                backdropFilter: "blur(12px)",
                                WebkitBackdropFilter: "blur(12px)",
                                display: "inline-block",
                                fontSize: "10px",
                                color: "#b3b3b3",
                              }}
                            >
                              {card.size}
                            </span>
                            <span
                              style={{
                                padding: "0.2rem 0.8rem",
                                borderRadius: "999px",
                                background: "rgba(35,35,35,0.35)",
                                border: "1px solid rgba(40,40,40,0.7)",
                                fontVariantNumeric: "tabular-nums",
                                backdropFilter: "blur(12px)",
                                WebkitBackdropFilter: "blur(12px)",
                                display: "inline-block",
                                fontSize: "10px",
                                color: "#b3b3b3",
                              }}
                            >
                              {formatTimeAgo(card.time)}
                            </span>
                            <span
                              className="uppercase tracking-wide"
                              style={{
                                padding: "0.2rem 0.8rem",
                                borderRadius: "999px",
                                background: "rgba(35,35,35,0.35)",
                                border: "1px solid rgba(40,40,40,0.7)",
                                fontVariantNumeric: "tabular-nums",
                                backdropFilter: "blur(12px)",
                                WebkitBackdropFilter: "blur(12px)",
                                display: "inline-block",
                                fontSize: "10px",
                                color: "#b3b3b3",
                              }}
                            >
                              {card.badge}
                            </span>
                          </div>
                        </div>

                        {/* Miniature Image with Fade */}
                        <div className="absolute top-0 right-0 bottom-0 w-44 overflow-hidden rounded-r-xl -my-4 -mr-4">
                          <img
                            src={primaryImageUrl}
                            alt={card.title}
                            className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity duration-500"
                          />
                          <div
                            className="absolute inset-y-0 left-0 w-32"
                            style={{
                              background:
                                "linear-gradient(to right, #181818, rgba(24,24,24,0.8), transparent)",
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ) : layout === "grid" ? (
                    /* GRID VIEW - только картинка и заголовок */
                    <div
                      className="rounded-xl shadow-lg overflow-hidden border content-card"
                      style={{
                        backgroundColor: "#303030",
                        borderColor: "#282828",
                        boxShadow: "0 4px 24px 0 rgba(0,0,0,0.18)",
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          paddingBottom: "120%",
                          position: "relative",
                        }}
                      >
                        <div
                          className="absolute inset-0 w-full h-full flex flex-col justify-end"
                          style={{
                            width: "100%",
                            height: "100%",
                            position: "absolute",
                            left: 0,
                            top: 0,
                          }}
                        >
                          {/* Image */}
                          <img
                            src={primaryImageUrl}
                            alt={card.title}
                            className="w-full object-cover opacity-90 transition-opacity duration-300 group-hover:opacity-100"
                            style={{
                              width: "100%",
                              height: "75%",
                              position: "absolute",
                              left: 0,
                              top: 0,
                              zIndex: 1,
                            }}
                          />

                          {/* Gradient overlay */}
                          <div
                            className="absolute left-0 bottom-0 w-full"
                            style={{
                              height: "50%",
                              background:
                                "linear-gradient(0deg, rgba(24,24,24,0.99) 50%, rgba(24,24,24,0.8) 70%, rgba(24,24,24,0) 100%)",
                              pointerEvents: "none",
                              zIndex: 2,
                            }}
                          />

                          {/* Title only */}
                          <div
                            className="absolute bottom-0 left-0 right-0 p-3 z-10"
                            style={{ pointerEvents: "none", zIndex: 3 }}
                          >
                            <h3
                              className="font-bold text-white text-sm leading-tight group-hover:text-orange-400 transition-colors"
                              style={{
                                textShadow: "0 1px 6px rgba(0,0,0,0.9)",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {card.title}
                            </h3>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* STACK VIEW - полный стиль как в основном окне */
                    <div
                      className="rounded-xl shadow-lg overflow-hidden border content-card"
                      style={{
                        backgroundColor: "#303030",
                        borderColor: "#282828",
                        boxShadow: "0 4px 24px 0 rgba(0,0,0,0.18)",
                      }}
                    >
                      {/* Обертка с увеличенной высотой */}
                      <div
                        style={{
                          width: "100%",
                          paddingBottom: "78.75%",
                          position: "relative",
                        }}
                      >
                        <div
                          className="absolute inset-0 w-full h-full flex flex-col justify-end"
                          style={{
                            width: "100%",
                            height: "100%",
                            position: "absolute",
                            left: 0,
                            top: 0,
                          }}
                        >
                          {/* Image */}
                          <div
                            style={{
                              width: "100%",
                              height: "71.4%",
                              position: "absolute",
                              left: 0,
                              top: 0,
                            }}
                          >
                            <TorrentImageGallery
                              images={cardImageUrls}
                              alt={card.title}
                              className="w-full h-full"
                              imageClassName="w-full h-full object-cover opacity-90 transition-opacity duration-300 group-hover:opacity-100"
                              imageStyle={{
                                height: "100%",
                                objectFit: "cover",
                              }}
                              dotContainerStyle={{ bottom: 40 }}
                            />
                          </div>

                          {/* Gradient overlay */}
                          <div
                            className="absolute left-0 bottom-0 w-full"
                            style={{
                              height: "60%",
                              background:
                                "linear-gradient(0deg, rgba(24,24,24,0.99) 60%, rgba(24,24,24,0.8) 72%, rgba(24,24,24,0.6) 82%, rgba(24,24,24,0) 100%)",
                              pointerEvents: "none",
                              zIndex: 2,
                            }}
                          />

                          {/* Content overlay */}
                          <div
                            className="absolute bottom-0 left-0 right-0 p-4 pb-6 pt-6 gap-2 flex flex-col z-10"
                            style={{
                              pointerEvents: "none",
                              zIndex: 3,
                              bottom: "0px",
                            }}
                          >
                            {/* Title - Moved up */}
                            <div
                              className="mb-1"
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                padding: "0 0.2rem",
                                maxWidth: "100%",
                                width: "fit-content",
                              }}
                            >
                              <span
                                className="font-bold"
                                style={{
                                  color: "#fff",
                                  textShadow:
                                    "0 2px 10px rgba(0,0,0,0.8), 0 0 2px rgba(0,0,0,0.5)",
                                  fontSize: "clamp(1.1rem, 4vw, 1.3rem)",
                                  lineHeight: "1.2",
                                  letterSpacing: "-0.01em",
                                }}
                              >
                                {card.title}
                              </span>
                            </div>

                            {/* Single line meta: Category • Time • Size */}
                            <div
                              className="flex items-center w-full px-1"
                              style={{
                                color: "rgba(255, 255, 255, 0.7)",
                                fontSize: "0.85rem",
                                fontWeight: "500",
                                letterSpacing: "0.02em",
                                textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                                fontFamily:
                                  "SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
                              }}
                            >
                              <span>{card.badge}</span>
                              <span className="mx-2 opacity-60">•</span>
                              <span>{formatTimeAgo(card.time)}</span>
                              <span className="mx-2 opacity-60">•</span>
                              <span>{card.size}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </LayoutGroup>
    </div>
  );
}
